import type { ElementType, ReactNode } from 'react';
import type { PolymorphicProps } from '#pie/constants/interfaces';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { cn, tv } from '#pie/utils/TailwindUtils';

export type CardProps<TComponent extends ElementType = 'section'> = {
  heading?: ReactNode;
  children: ReactNode;
  className?: string;
  headingUnderline?: boolean;
} & PolymorphicProps<TComponent, VariantProps<typeof styles>>;

export function Card<TComponent extends ElementType = 'section'>({
  children,
  className,
  heading,
  hasBorder,
  size,
  variant = 'base',
  headingUnderline = true,
}: CardProps<TComponent>) {
  const s = styles({ hasBorder, headingUnderline, size, variant });
  return (
    <section className={cn(s.base(), className)}>
      {heading && <header className={s.heading()}>{heading}</header>}
      {children}
    </section>
  );
}

const styles = tv({
  base: 'bg-white',
  slots: {
    heading: 'max-h-14 mb-4',
  },
  variants: {
    variant: {
      base: 'rounded-card-default',
      interactive: 'rounded-card-interactive hover:shadow-card cursor-pointer',
    },
    hasBorder: {
      base: ' border border-border-divider',
      error: 'border border-l-error border-l-4',
      success: 'border border-l-success border-l-4',
    },
    headingUnderline: {
      false: {
        heading: '',
      },
      true: {
        heading: 'border-b border-border-divider',
      },
    },
    size: {
      lg: {
        base: 'p-6 pb-2',
        heading: 'pt-4 -mt-6',
      },
      md: {
        base: 'p-6',
        heading: '-mt-6 py-4',
      },
    },
  },
});
